import { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Link } from 'react-router-dom';
import { Typography, Collapse, makeStyles } from '@material-ui/core';
import TextBoxWithTooltip from '../../../textBoxWithTooltip';
import StatusIndicator from '../../../statusIndicator';
import CustomButton from '../../../customButton';
import ActionButton from '../../../actionButton';
import DotsMenu from '../../../dotsMenu';
import SurveyType from '../../../surveyType';
import Cell from './cell';
import Review from './review';
import {
  getColumnWidth,
  getAlignItem,
} from '../../../../../constants/gridTable';
import { ACTION_BUTTON_TYPES } from '../../../actionButton/config';
import { ellipsis } from '../../../../../constants/helperCssRules';
import { isArrayEmpty } from '../../../../../utility/helpers';
import {
  prepareCycleReviewMenuItems,
  prepareCycleReviewTableHeaders,
  prepareCycleReviewTableHeaderActions,
  prepareCycleReviewsData,
} from '../../../../../utility/cycles';

const useStyles = makeStyles(
  ({ palette: { primary }, spacing, breakpoints }) => ({
    root: {
      backgroundColor: primary.white,
      display: 'grid',
      width: '100%',
      transition: 'background-color .3s ease',
    },
    rowHeader: {
      backgroundColor: primary.white,
      display: 'grid',
      transition: 'background-color .3s ease',
      '&:hover': {
        backgroundColor: ({ isExpanded }) =>
          isExpanded ? primary.white : primary.bluish9,
      },
    },
    rowCell: {
      alignSelf: 'center',
      boxSizing: 'border-box',
      padding: spacing(4, 2),
      color: primary.bluish1,
      position: 'relative',
      textDecoration: 'none',
      ...ellipsis(),

      [breakpoints.up('xLg')]: {
        padding: spacing(4),
      },
    },
    actionCell: {
      cursor: 'default',
    },
    reviewsWrapper: {
      boxSizing: 'border-box',
      borderTop: `1px solid ${primary.bluish5}`,
      marginBottom: spacing(4),
      width: '100%',
    },
    reviewHeader: {
      backgroundColor: `${primary.white}`,
      display: 'grid',
      alignItems: 'center',
      gridTemplateColumns:
        'minmax(50px, 50px) minmax(132px, 1.5fr) minmax(90px, 1fr) minmax(90px, 1fr) minmax(160px, 1fr) minmax(60px, 90px)',
    },
    reviewHeaderCell: {
      boxSizing: 'border-box',
      padding: spacing(1.5, 2),
      textAlign: 'left',
      lineHeight: '24px',
      userSelect: 'none',
      [breakpoints.up('xLg')]: {
        padding: spacing(1.5, 4),
      },
    },
    reviewActionCell: {
      textAlign: 'right',
    },
    reviewTypeTooltip: {
      cursor: 'pointer',
    },
    typeIcon: {
      marginRight: 0,
    },
    reviewTitleWrapper: {
      display: 'flex',
      alignItems: 'center',
      maxWidth: '100%',
    },
    title: {
      width: 'auto',
    },
    fillInlineButtonWrapper: {
      padding: spacing(2.5, 2),
      [breakpoints.up('xLg')]: {
        padding: spacing(2.5, 4),
      },
    },
    fillReviewButton: {
      border: `1px solid ${primary.bluish1}`,
      borderRadius: 48,
      color: primary.bluish1,
      fontFamily: 'ProximaNova-Bold',
      padding: spacing(2, 4),
      maxHeight: 32,
    },
    buttonIconWrapper: {
      marginLeft: 0,
      marginRight: spacing(1),
    },
    buttonIcon: {
      height: 16,
      width: 16,
    },
    statusWrapper: {
      display: 'flex',
      alignItems: 'center',
      padding: spacing(3, 2),
      [breakpoints.up('xLg')]: {
        padding: spacing(3, 4),
      },
    },
    completedValue: {
      marginLeft: spacing(2),
    },
    reviewActions: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-end',
    },
    dotsMenu: {
      width: 30,
      height: 30,
    },
  })
);

const Row = ({
  translations,
  data,
  columns,
  position,
  reviewTypes,
  cellsTooltips = [],
  canManage,
  getCellRef,
  onViewReviews,
  prepareReviewDetailsUrl,
  onFillReview,
  onSendReminder,
  onRenameReview,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const classes = useStyles({ isExpanded });
  const { id, isHighlighted, isDisabled, reviews } = data;
  const hasReviews = !isArrayEmpty(reviews);

  const handleToggleExpand = () => {
    if (isExpanded) {
      return setIsExpanded(false);
    }

    setIsExpanded(true);
    return onViewReviews(data.id);
  };

  const onColumnClick = isHeaderAction => e => {
    if (isHeaderAction) {
      e.preventDefault();
      e.stopPropagation();
    }
  };

  const handleFillReview = reviewId => e => {
    e.preventDefault();
    e.stopPropagation();
    return onFillReview(reviewId);
  };

  const renderReviewType = type => (
    <SurveyType
      typeIconClass={classes.typeIcon}
      tooltipClass={classes.reviewTypeTooltip}
      type={type}
      isIconStatus
    />
  );

  const renderReviewTitle = review => (
    <div className={classes.reviewTitleWrapper}>
      <TextBoxWithTooltip
        className={classes.title}
        text={review.title}
        variant="subtitle1"
      />
    </div>
  );

  const renderStatus = (review, status, type) => {
    const { icon: Icon } = type;

    if (review.review_link_available) {
      return (
        <div className={classes.fillInlineButtonWrapper}>
          <CustomButton
            className={classes.fillReviewButton}
            classes={{ startIcon: classes.buttonIconWrapper }}
            type="addWithTextRounded"
            startIcon={<Icon className={classes.buttonIcon} />}
            onClick={handleFillReview(review.id)}
          >
            {translations.menuItemLabels.fillReview}
          </CustomButton>
        </div>
      );
    }

    return (
      <div className={classes.statusWrapper}>
        <StatusIndicator status={status} />
        <Typography className={classes.completedValue}>
          {review.completed}
        </Typography>
      </div>
    );
  };

  const renderActions = review => {
    const menuItems = canManage
      ? prepareCycleReviewMenuItems(
          translations.reviewMenu,
          review,
          onSendReminder,
          onRenameReview(data.id)
        )
      : [];

    return (
      <div className={classes.reviewActions}>
        <ActionButton
          type={ACTION_BUTTON_TYPES.REPORT}
          tooltipText={translations.viewReport}
          linkProps={{
            to: `/cycles/${id}/reviews/${review.id}/report`,
          }}
          isLink
        />
        {canManage && (
          <DotsMenu iconButtonClass={classes.dotsMenu} menuItems={menuItems} />
        )}
      </div>
    );
  };

  const reviewActions = prepareCycleReviewTableHeaderActions(
    translations.reviewColumns
  );

  const reviewColumns = prepareCycleReviewTableHeaders(
    translations.reviewColumns
  );

  const reviewsTableData = prepareCycleReviewsData(
    reviews,
    translations,
    reviewTypes,
    renderReviewType,
    renderReviewTitle,
    renderStatus,
    renderActions
  );

  return (
    <div className={classes.root}>
      <div className={classes.rowHeader} style={getColumnWidth(columns)}>
        {columns.map(column => (
          <Link
            key={`row_cell_${id}_${column.id}`}
            id={column.id}
            style={getAlignItem(column)}
            className={classNames(
              classes.rowCell,
              'row-cell',
              { [classes.actionCell]: column.isHeaderAction },
              column.rowCellClass
            )}
            to={`/cycles/${data.id}/details`}
            onClick={onColumnClick(column.isHeaderAction)}
          >
            <Cell
              translations={translations}
              value={data[column.rowKey]}
              column={column}
              position={position}
              isHighlighted={column.isHighlighted || isHighlighted}
              shouldShowTooltip={
                column.hasConditionalTooltip &&
                cellsTooltips?.[position]?.[column.rowKey]
              }
              isDisabled={!!isDisabled}
              isExpanded={isExpanded}
              getCellRef={getCellRef}
              onToggleExpand={handleToggleExpand}
            />
          </Link>
        ))}
      </div>
      <Collapse in={isExpanded && hasReviews} unmountOnExit>
        <div className={classes.reviewsWrapper}>
          <div className={classes.reviewHeader}>
            {[...reviewColumns, ...reviewActions].map(column => (
              <Typography
                className={classNames(classes.reviewHeaderCell, {
                  [classes.reviewActionCell]: column.isHeaderAction,
                })}
                component="div"
                variant="overline"
                key={`header_cell_${column.id}`}
              >
                {column.title}
              </Typography>
            ))}
          </div>
          {reviewsTableData.map(review => (
            <Review
              key={`review_item_${review.id}`}
              id={review.id}
              translations={translations}
              review={review}
              detailsUrl={prepareReviewDetailsUrl(id, review.id)}
            />
          ))}
        </div>
      </Collapse>
    </div>
  );
};

Row.propTypes = {
  translations: PropTypes.object.isRequired,
  data: PropTypes.object.isRequired,
  columns: PropTypes.arrayOf(PropTypes.object).isRequired,
  position: PropTypes.number.isRequired,
  cellsTooltips: PropTypes.arrayOf(PropTypes.object),
  canManage: PropTypes.bool.isRequired,
  getCellRef: PropTypes.func.isRequired,
  onViewReviews: PropTypes.func.isRequired,
  prepareReviewDetailsUrl: PropTypes.func.isRequired,
  onSendReminder: PropTypes.func.isRequired,
  onFillReview: PropTypes.func.isRequired,
  onRenameReview: PropTypes.func.isRequired,
};

export default Row;
